import { useEffect, useState, useCallback } from 'react'
import { adminService } from 'core/services/adminService'
import { ReferralUser } from './referral-user'
import { MilestonesList } from './milestones-list'
import { formatNumbers } from 'core/helpers/format-numbers'

import c from './assets/sass/referrals-list.module.scss'

export const ReferralsList = () => {
    const [view, setView] = useState('users')

    return (
        <div className={c.component}>
            <div className={c.tabs}>
                <button
                    className={view === 'users' ? c.tabActive : c.tab}
                    onClick={() => setView('users')}
                >
                    Топ по рефералам
                </button>
                <button
                    className={view === 'milestones' ? c.tabActive : c.tab}
                    onClick={() => setView('milestones')}
                >
                    Награды
                </button>
                <button
                    className={view === 'bot-detection' ? c.tabActive : c.tab}
                    onClick={() => setView('bot-detection')}
                >
                    🤖 Детекция ботов
                </button>
            </div>

            {view === 'users' && <ReferralUsersView />}
            {view === 'milestones' && <MilestonesList />}
            {view === 'bot-detection' && <BotDetectionView />}
        </div>
    )
}

const ReferralUsersView = () => {
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const [selectedUser, setSelectedUser] = useState(null)

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true)
            const data = await adminService.getReferralStats({
                page,
                limit: 20,
            })
            setUsers(data.users)
            setTotal(data.pagination.total)
        } catch (error) {
            console.error('Failed to load referral stats:', error)
        } finally {
            setLoading(false)
        }
    }, [page])

    useEffect(() => {
        loadUsers()
    }, [loadUsers])

    if (selectedUser) {
        return (
            <ReferralUserDetails
                user={selectedUser}
                onBack={() => setSelectedUser(null)}
            />
        )
    }

    if (loading) return <div className={c.loading}>Загрузка...</div>

    return (
        <>
            <div className={c.list}>
                {users.map((user) => (
                    <ReferralUser
                        key={user.id}
                        user={user}
                        onClick={() => setSelectedUser(user)}
                    />
                ))}
            </div>
            {total > 20 && (
                <div className={c.pagination}>
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        ← Назад
                    </button>
                    <span>Страница {page}</span>
                    <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page >= Math.ceil(total / 20)}
                    >
                        Вперед →
                    </button>
                </div>
            )}
        </>
    )
}

const ReferralUserDetails = ({ user, onBack }) => {
    const [referrals, setReferrals] = useState([])
    const [totalReferrals, setTotalReferrals] = useState(0)
    const [pagination, setPagination] = useState(null)
    const [progress, setProgress] = useState(null)
    const [earnings, setEarnings] = useState(null)
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const LIMIT = 50

    const loadDetails = useCallback(async () => {
        try {
            setLoading(true)
            const data = await adminService.getUserReferrals(user.id, {
                page,
                limit: LIMIT,
            })
            setReferrals(data.referrals || [])
            setTotalReferrals(data.totalReferrals || data.referrals?.length || 0)
            setPagination(data.pagination || null)
            setProgress(data.progress)
            setEarnings(data.earnings)
        } catch (error) {
            console.error('Failed to load referral details:', error)
        } finally {
            setLoading(false)
        }
    }, [user.id, page])

    useEffect(() => {
        loadDetails()
    }, [loadDetails])

    const displayName = user.username || user.firstName || 'Пользователь'
    const avatar = user.photoUrl || '/images/avatar.png'
    const firstLetter = displayName.charAt(0).toUpperCase()

    const botNames = ['Fillerdriller', 'ggturbobot1', 'ggturbobot2', 'ggturbobot3', 'ggturbobot4', 'ggturbobot5']
    const botsCount = referrals.filter(
        (r) => botNames.includes(r.username) || parseInt(r.telegramId) < 0
    ).length
    const realUsersCount = referrals.length - botsCount
    const newUsersCount = referrals.filter((r) => {
        const created = new Date(r.createdAt)
        const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
        return created >= dayAgo
    }).length

    const totalPages = pagination ? pagination.pages : 1

    if (loading && page === 1) return <div className={c.loading}>Загрузка...</div>

    return (
        <div className={c.details}>
            <button className={c.backBtn} onClick={onBack}>
                ← Назад к топу
            </button>

            <div className={c.detailsHeader}>
                {user.photoUrl ? (
                    <img
                        src={avatar}
                        alt={displayName}
                        className={c.detailsAvatar}
                    />
                ) : (
                    <div className={c.detailsAvatarPlaceholder}>
                        {firstLetter}
                    </div>
                )}
                <div className={c.detailsInfo}>
                    <span className={c.detailsName}>{displayName}</span>
                    <span className={c.detailsTgId}>
                        TG ID: {user.telegramId}
                    </span>
                </div>
            </div>

            <div className={c.statsGrid}>
                <div className={c.statsCard}>
                    <span className={c.statsLabel}>Всего рефералов</span>
                    <span className={c.statsValue}>{formatNumbers(totalReferrals)}</span>
                </div>
                <div className={c.statsCard}>
                    <span className={c.statsLabel}>На странице</span>
                    <span className={c.statsValue}>{realUsersCount}</span>
                </div>
                <div className={c.statsCard}>
                    <span className={c.statsLabel}>Ботов (стр.)</span>
                    <span className={c.statsValue}>{botsCount}</span>
                </div>
                <div className={c.statsCard}>
                    <span className={c.statsLabel}>Новых (24ч, стр.)</span>
                    <span className={c.statsValue}>{newUsersCount}</span>
                </div>
                <div className={c.statsCard}>
                    <span className={c.statsLabel}>Уровень</span>
                    <span className={c.statsValue}>
                        {progress?.currentLevel || 1}
                    </span>
                </div>
                <div className={c.statsCard}>
                    <span className={c.statsLabel}>Заработано</span>
                    <span className={c.statsValue}>
                        {formatNumbers(parseInt(earnings?.totalEarned || 0))}
                    </span>
                </div>
            </div>

            <h4 className={c.listTitle}>
                Список рефералов ({formatNumbers(totalReferrals)})
            </h4>

            {loading ? (
                <div className={c.loading}>Загрузка...</div>
            ) : (
                <div className={c.referralsList}>
                    {referrals.map((ref) => {
                        const refName = ref.username || ref.firstName || 'N/A'
                        const refAvatar = ref.photoUrl || '/images/avatar.png'
                        const refLetter = refName.charAt(0).toUpperCase()
                        const isBot =
                            botNames.includes(ref.username) ||
                            parseInt(ref.telegramId) < 0

                        return (
                            <div key={ref.id} className={c.refItem}>
                                {ref.photoUrl ? (
                                    <img
                                        src={refAvatar}
                                        alt={refName}
                                        className={c.refAvatar}
                                    />
                                ) : (
                                    <div className={c.refAvatarPlaceholder}>
                                        {refLetter}
                                    </div>
                                )}
                                <div className={c.refInfo}>
                                    <span className={c.refName}>
                                        {refName} {isBot && '🤖'}
                                    </span>
                                    <span className={c.refTgId}>
                                        TG ID: {ref.telegramId}
                                    </span>
                                    <span className={c.refBalance}>
                                        💰{' '}
                                        {formatNumbers(parseInt(ref.balance || 0))}
                                    </span>
                                </div>
                                <div className={c.refDate}>
                                    {new Date(ref.createdAt).toLocaleDateString(
                                        'ru-RU'
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div className={c.pagination}>
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                    >
                        ← Назад
                    </button>
                    <span>Страница {page} из {totalPages}</span>
                    <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page >= totalPages || loading}
                    >
                        Вперед →
                    </button>
                </div>
            )}
        </div>
    )
}

const BotDetectionView = () => {
    const [userId, setUserId] = useState('')
    const [stats, setStats] = useState(null)
    const [suspiciousUsers, setSuspiciousUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [revoking, setRevoking] = useState(false)

    const handleDetect = async () => {
        const trimmedId = userId.trim()

        if (!trimmedId) {
            alert('Введите User ID пользователя')
            return
        }

        // Валидация формата User ID (32 символа хэш)
        if (trimmedId.length !== 32 || !/^[a-f0-9]{32}$/.test(trimmedId)) {
            alert(
                '❌ Неверный формат User ID!\n\nUser ID должен быть 32-символьный хэш (например: 04dd73a7ff11153a93f1c5a72cd1053c)\n\nЭто НЕ Telegram ID! Скопируйте User ID из таба "Топ по рефералам"'
            )
            return
        }

        try {
            setLoading(true)
            setSuspiciousUsers([])
            setStats(null)

            // Добавляем таймаут для больших баз (10000+ рефералов может занять 10-20 сек)
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(
                    () =>
                        reject(
                            new Error(
                                'Таймаут запроса (30 сек). Попробуйте еще раз.'
                            )
                        ),
                    30000
                )
            )

            const data = await Promise.race([
                adminService.detectBotReferrals(trimmedId),
                timeoutPromise,
            ])

            if (!data || !data.stats) {
                throw new Error('Некорректный ответ от сервера')
            }

            setStats(data.stats)
            setSuspiciousUsers(data.suspiciousUsers || [])

            if (data.stats.total === 0) {
                alert('ℹ️ У этого пользователя нет рефералов')
            }
        } catch (error) {
            console.error('Failed to detect bots:', error)
            setStats(null)
            setSuspiciousUsers([])

            const errorMsg =
                error.response?.data?.error ||
                error.message ||
                'Неизвестная ошибка'
            alert(
                '❌ Ошибка детекции:\n\n' +
                    errorMsg +
                    '\n\nПроверьте что User ID правильный (32 символа)'
            )
        } finally {
            setLoading(false)
        }
    }

    const handleRevoke = async () => {
        if (!userId.trim()) {
            alert('Введите ID пользователя')
            return
        }

        if (
            !window.confirm(
                `Отозвать незаслуженные награды?\n\nЭто отзовет награды за всех рефералов с < 5 тапов.`
            )
        ) {
            return
        }

        try {
            setRevoking(true)
            const data = await adminService.revokeUndeservedRewards(
                userId.trim()
            )
            alert(
                `✅ Готово!\n\nОтозвано: ${formatNumbers(parseInt(data.revokedAmount || 0))} монет\nРефералов: ${data.revokedCount}`
            )

            handleDetect()
        } catch (error) {
            console.error('Failed to revoke rewards:', error)
            alert(
                'Ошибка отзыва: ' +
                    (error.response?.data?.error || error.message)
            )
        } finally {
            setRevoking(false)
        }
    }

    return (
        <div className={c.botDetection}>
            <div className={c.searchSection}>
                <h3 className={c.sectionTitle}>
                    🔍 Проверка пользователя на бот-ферму
                </h3>
                <div className={c.searchBox}>
                    <input
                        type="text"
                        placeholder="User ID (скопируйте из таба 'Топ по рефералам' нажав на User ID)"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        className={c.searchInput}
                        onKeyPress={(e) => e.key === 'Enter' && handleDetect()}
                    />
                    <button
                        onClick={handleDetect}
                        disabled={loading || !userId.trim()}
                        className={c.detectBtn}
                    >
                        {loading ? '⏳ Проверка...' : '🔍 Проверить'}
                    </button>
                </div>
                <p className={c.hint}>
                    💡 User ID это не Telegram ID! Найдите пользователя в табе
                    "Топ по рефералам" и скопируйте его User ID (оранжевая
                    строка с 📋)
                </p>
            </div>

            {stats && (
                <>
                    <div className={c.statsSection}>
                        <h3 className={c.sectionTitle}>
                            📊 Статистика рефералов
                        </h3>
                        <div className={c.statsGrid}>
                            <div className={c.statsCard}>
                                <span className={c.statsLabel}>
                                    Всего рефералов
                                </span>
                                <span className={c.statsValue}>
                                    {formatNumbers(stats.total)}
                                </span>
                            </div>
                            <div className={c.statsCard}>
                                <span className={c.statsLabel}>
                                    Активных (≥5 тапов)
                                </span>
                                <span
                                    className={c.statsValue}
                                    style={{ color: '#4CAF50' }}
                                >
                                    {formatNumbers(stats.active)}
                                </span>
                            </div>
                            <div className={c.statsCard}>
                                <span className={c.statsLabel}>
                                    Подозрительных
                                </span>
                                <span
                                    className={c.statsValue}
                                    style={{ color: '#f44336' }}
                                >
                                    {formatNumbers(stats.suspicious)}
                                </span>
                            </div>
                        </div>

                        <div
                            className={c.statsCard}
                            style={{ marginTop: '12px' }}
                        >
                            <span className={c.statsLabel}>Процент ботов</span>
                            <span
                                className={c.statsValue}
                                style={{
                                    fontSize: '32px',
                                    color:
                                        parseFloat(stats.botPercentage) > 50
                                            ? '#f44336'
                                            : parseFloat(stats.botPercentage) >
                                                20
                                              ? '#FFA726'
                                              : '#4CAF50',
                                }}
                            >
                                {stats.botPercentage}%
                            </span>
                        </div>

                        {parseFloat(stats.botPercentage) > 50 && (
                            <div className={c.warningBox}>
                                <span className={c.warningIcon}>⚠️</span>
                                <div className={c.warningContent}>
                                    <strong className={c.warningTitle}>
                                        Подозрительная активность!
                                    </strong>
                                    <p className={c.warningText}>
                                        Более 50% рефералов не проявляют
                                        активность. Вероятная бот-ферма.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className={c.actionsSection}>
                            <button
                                onClick={handleRevoke}
                                disabled={revoking || stats.suspicious === 0}
                                className={c.revokeBtn}
                            >
                                {revoking
                                    ? '⏳ Отзываем награды...'
                                    : `💰 Отозвать незаслуженные награды`}
                            </button>
                            <p className={c.actionHint}>
                                Отзовет награды за {stats.suspicious} неактивных
                                рефералов
                            </p>
                        </div>
                    </div>

                    {suspiciousUsers.length > 0 && (
                        <div className={c.usersSection}>
                            <h3 className={c.sectionTitle}>
                                🚨 Подозрительные рефералы (показаны первые 50
                                из {suspiciousUsers.length})
                            </h3>
                            <div className={c.suspiciousList}>
                                {suspiciousUsers.slice(0, 50).map((user) => {
                                    const displayName =
                                        user.username || user.firstName || 'N/A'
                                    const firstLetter = displayName
                                        .charAt(0)
                                        .toUpperCase()

                                    return (
                                        <div
                                            key={user.id}
                                            className={c.suspiciousItem}
                                        >
                                            <div className={c.suspiciousAvatar}>
                                                {firstLetter}
                                            </div>
                                            <div className={c.suspiciousInfo}>
                                                <span
                                                    className={c.suspiciousName}
                                                >
                                                    {displayName}
                                                </span>
                                                <span
                                                    className={c.suspiciousTgId}
                                                >
                                                    TG: {user.telegramId}
                                                </span>
                                            </div>
                                            <div className={c.suspiciousStats}>
                                                <span
                                                    className={
                                                        c.suspiciousBalance
                                                    }
                                                >
                                                    💰{' '}
                                                    {formatNumbers(
                                                        parseInt(user.balance)
                                                    )}
                                                </span>
                                                <span
                                                    className={c.suspiciousTaps}
                                                >
                                                    ⚡ {user.tapCount} тапов
                                                </span>
                                            </div>
                                            <div className={c.suspiciousDate}>
                                                {new Date(
                                                    user.createdAt
                                                ).toLocaleDateString('ru-RU', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            {suspiciousUsers.length > 50 && (
                                <p className={c.moreUsers}>
                                    + еще {suspiciousUsers.length - 50}{' '}
                                    подозрительных...
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}

            {!stats && (
                <div className={c.emptyState}>
                    <span className={c.emptyIcon}>🤖</span>
                    <p className={c.emptyTitle}>Детекция бот-ферм</p>
                    <p className={c.emptyText}>
                        Введите ID пользователя из топа рефералов для проверки
                        на ботов
                    </p>
                    <div className={c.emptyFeatures}>
                        <div className={c.emptyFeature}>
                            <span>✓</span> Автоматический подсчет неактивных
                            рефералов
                        </div>
                        <div className={c.emptyFeature}>
                            <span>✓</span> Детальная статистика по каждому
                            рефералу
                        </div>
                        <div className={c.emptyFeature}>
                            <span>✓</span> Отзыв незаслуженных наград одной
                            кнопкой
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
